import { APP_BASE_HREF } from '@angular/common';
import { inject, Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { checkUrlAbsolute, concatUrl, openUrl, resolveValue } from '@lvflanders/bar';

import { BaasInteractiveElementOptions } from './baas-interactive-element-options';

// NOTE do not expose
/** Internal provider containing centralized behavior of clickable or tapable elements (interactive). */
@Injectable({ providedIn: 'root' })
export class _BarInteractiveElementBehavior {
    readonly #domSanitizer = inject(DomSanitizer);
    readonly #router = inject(Router);
    readonly #baseHref = inject<string>(APP_BASE_HREF, { optional: true });

    /** Invoked by button tags. */
    async buttonClick(event: any, item: BaasInteractiveElementOptions) {
        if (item.action) {
            const actionResult = item.action(event);
            if (actionResult != null) {
                if (actionResult instanceof Promise) await actionResult;
                // allow action to return true to also allow default link behavior
                // by default if there is an action we prevent further execution (with undefined return value)
                else if (typeof actionResult === 'boolean' && !actionResult) return;
            }
        }
        // NOTE no else because we could perfectly invoke an action and then navigating (if not true returned to mark as handled by the action
        if (item.routerCommands) this.#router.navigate(item.routerCommands, { queryParams: item.queryParams, fragment: item.fragment });
        else if (item.routerUrl) this.#router.navigateByUrl(item.routerUrl);
        else if (item.href) openUrl(item.href, item.target);
    }

    /** Invoked by anchor tags without routerLink. */
    async linkClick(
        event: any,
        item: {
            action?: (event: any) => void | boolean | Promise<void>;
            routerUrl?: string | (() => string);
            href?: string | (() => string) | SafeUrl | (() => SafeUrl) | SafeResourceUrl | (() => SafeResourceUrl);
        }
    ) {
        let clickExecuted = false;

        if (item.action) {
            const actionResult = item.action(event);
            if (actionResult instanceof Promise) await actionResult;
            // allow action to return true to also allow default link behavior
            // by default if there is an action we prevent further execution (with undefined return value)
            clickExecuted = actionResult != null && typeof actionResult === 'boolean' ? !(<boolean>actionResult) : true;
        } else if (event.ctrlKey) {
            let url: string = null;
            if (item.routerUrl) {
                const routerUrl = resolveValue(item.routerUrl);
                if (checkUrlAbsolute(routerUrl)) {
                    url = routerUrl;
                } else {
                    url = concatUrl(this.#baseHref || '/', routerUrl);
                }
            }
            // NOTE that for routerLink behavior is default
            else if (item.href) {
                const href = resolveValue(item.href);
                if (href) url = typeof href === 'string' ? href : this.#domSanitizer.sanitize(SecurityContext.URL, href);
            }

            if (url) {
                openUrl(url, '_blank');
                clickExecuted = true;
            }
        } else if (item.routerUrl) {
            const routerUrl = resolveValue(item.routerUrl);
            this.#router.navigateByUrl(routerUrl);
            clickExecuted = true;
        }
        // in case a href has been execute, we don't want it to be executed
        if (clickExecuted && (item.href || item.routerUrl)) {
            event.preventDefault();
        }
    }

    async linkClickWithRouterCommands(event, item: { action?: (event: any) => void | boolean | Promise<void> }): Promise<void> {
        if (item.action) {
            // we invoke the action
            const result = item.action(event);
            if (result instanceof Promise) await result;
            // consumer can define whether he handled the click meaning routerLink has to be prevented
            if (result === false) {
                event.stopPropagation();
                event.preventDefault();
            } else if (result instanceof Promise) await result;
        }
    }
}
